import { useState, useEffect } from 'react';
import { getAdminGlobalStats, type AdminGlobalStats } from '@/api/marketplace-analytics.api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const StatsCard = ({
  title,
  value,
  description,
}: {
  title: string;
  value: number | string;
  description?: string;
}) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      {description && (
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      )}
    </CardContent>
  </Card>
);

export default function AnalyticsOverview() {
  const [stats, setStats] = useState<AdminGlobalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const data = await getAdminGlobalStats();
        console.log(data,' global data')
        setStats(data);
      } catch (err) {
        setError('Failed to load global statistics.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Marketplace/Exchange Analytics</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-24" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Marketplace/Exchange Analytics</h1>
        </div>
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Marketplace/Exchange Analytics</h1>
      </div>

      {/* Listing Stats */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Listing Statistics</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <StatsCard title="Active Listings" value={stats.activeListings} description="Currently live listings" />
          <StatsCard title="Marketplace Active" value={stats.marketplaceActive} description="Active for-sale listings" />
          <StatsCard title="Exchange Active" value={stats.exchangeActive} description="Active for-exchange listings" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
          <StatsCard title="Sold Listings" value={stats.soldListings} description="Total sold items" />
          <StatsCard title="Exchanged Listings" value={stats.exchangedListings} description="Total exchanged items" />
          <StatsCard title="Total Listings" value={stats.totalListings} description="All-time listings created" />
        </div>
      </div>

      {/* Transaction Stats */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Transaction Statistics</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <StatsCard title="Total Sales" value={stats.totalSales} description="Marketplace sales completed" />
          <StatsCard title="Total Exchanges" value={stats.totalExchanges} description="Exchange transactions completed" />
          <StatsCard title="Total Transactions" value={stats.totalTransactions} description="All transactions" />
        </div>
      </div>

      {/* Engagement Stats */}
      <div>
        <h3 className="text-lg font-semibold mb-3">User Engagement</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <StatsCard title="Total Offers" value={stats.totalOffers} description="Offers made on listings" />
          <StatsCard title="Total Reviews" value={stats.totalReviews} description="Reviews submitted" />
          <StatsCard title="Total Favorites" value={stats.totalFavorites} description="Listings favorited" />
        </div>
      </div>
    </div>
  );
}
